import { useMemo } from 'react';

import useGet from 'lib/hooks/useGet';
import { ANNUALIZED_MFR_GMV_KPI_METRICS } from 'lib/networking/endpoints';

/**
 * useMfrKpiMetrics — top-of-page KPI metrics for the Manufacturer Sales Viewer.
 *
 * Hits GET /annualized-mfr-gmv/kpi-metrics (params: l1_category_id,
 * l2_category_id, distributor_id, manufacturer_id, brand_id). The backend applies
 * the deepest category filter (L2 over L1), matching the manufacturer grid.
 *
 * Two response shapes depending on scope:
 *   - Root (no manufacturerId): { annualized_gmv, manufacturer_count,
 *     avg_ecom_score, avg_attribute_score }.
 *   - Drilled into a manufacturer/brand (manufacturerId set): the Tyson-mockup
 *     detail set { annualized_gmv, customers, total_skus, active_skus, gmv_skus,
 *     avg_ecom_score, avg_attribute_score }.
 */
function useMfrKpiMetrics({
  l1CategoryId,
  l2CategoryId,
  distributorId,
  thirdPartyTag,
  manufacturerId,
  brandId,
  skip = false,
} = {}) {
  const params = useMemo(() => {
    const p = {};
    if (l1CategoryId) p.l1_category_id = l1CategoryId;
    if (l2CategoryId) p.l2_category_id = l2CategoryId;
    if (distributorId) p.distributor_id = distributorId;
    if (thirdPartyTag) p.third_party_tag = thirdPartyTag;
    if (manufacturerId) p.manufacturer_id = manufacturerId;
    if (brandId) p.brand_id = brandId;
    return p;
  }, [l1CategoryId, l2CategoryId, distributorId, thirdPartyTag, manufacturerId, brandId]);

  return useGet(ANNUALIZED_MFR_GMV_KPI_METRICS, params, skip);
}

export default useMfrKpiMetrics;
